import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { withTracking } from 'gw-portals-tracking-react';
import CommonConstants from 'sg-common-constants';
import styles from 'saga-styling/common-styles/widgets/TypeAheadComponent.module.scss';

function SgTypeAheadDropdownComponent(props) {
    SgTypeAheadDropdownComponent.propTypes = {
        items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        maxResults: PropTypes.number,
        onItemSelected: PropTypes.func.isRequired,
        onCancelSelection: PropTypes.func.isRequired,
        setupRef: PropTypes.func,
        topDropdownText: PropTypes.string,
        bottomDropdownText: PropTypes.string,
        id: PropTypes.string.isRequired
    };

    SgTypeAheadDropdownComponent.defaultProps = {
        maxResults: undefined,
        setupRef: undefined,
        topDropdownText: undefined,
        bottomDropdownText: undefined
    };

    const {
        items, maxResults, id, topDropdownText, bottomDropdownText
    } = props;

    const [activeItem, setActiveItem] = useState(undefined);

    useEffect(() => {
        if (activeItem) {
            const itemId = items[activeItem].code;
            const selectedItem = document.getElementById(itemId);
            selectedItem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }, [activeItem]);

    if (_.isEmpty(items)) {
        return null;
    }

    const chooseActiveItem = () => {
        const { onItemSelected } = props;
        if (!_.isNil(activeItem) && !_.isEmpty(items) && _.isFunction(onItemSelected)) {
            onItemSelected(items[activeItem]);
        }
    };

    const cancelSelection = () => {
        const { onCancelSelection } = props;
        setActiveItem(undefined);
        onCancelSelection();
    };

    const selectNextItem = () => {
        if (_.isNumber(activeItem) && activeItem < _.size(items) - 1) {
            setActiveItem(activeItem + 1);
        }
    };

    const selectPreviousItem = () => {
        if (_.isNumber(activeItem) && activeItem > 0) {
            setActiveItem(activeItem - 1);
        }
    };

    const selectItemOnHover = (index) => {
        setActiveItem(index);
    };

    const handleOnFocus = () => {
        // select first item on focus
        setActiveItem(0);
    };

    const handleKeyDown = (event) => {
        if (event.key !== CommonConstants.EVENT_KEYS.TAB_KEY) {
            // allow the user to select another
            event.preventDefault();
        }

        switch (event.key) {
            case CommonConstants.EVENT_KEYS.ARROW_UP_KEY:
                selectPreviousItem();
                break;
            case CommonConstants.EVENT_KEYS.ARROW_DOWN_KEY:
                selectNextItem();
                break;
            case CommonConstants.EVENT_KEYS.ENTER_KEY:
            case CommonConstants.EVENT_KEYS.TAB_KEY:
                chooseActiveItem();
                break;
            case CommonConstants.EVENT_KEYS.ESCAPE_KEY:
                cancelSelection();
                break;
            default: // do nothing
        }
    };

    const setupDropdownRef = (ref) => {
        const { setupRef } = props;
        if (!_.isNil(setupRef) && _.isFunction(setupRef)) {
            setupRef(ref);
        }
    };

    const renderDropdownElement = (item, index) => {
        const { renderAs, onItemSelected, onRenderItem } = props;
        let renderableItem;
        if (!_.isNil(renderAs)) {
            renderableItem = _.get(item, renderAs);
        } else if (!_.isNil(onRenderItem)) {
            renderableItem = onRenderItem(item);
        } else {
            renderableItem = item;
        }

        const isSelected = index === activeItem;

        const entryClassName = classNames(styles.typeaheadMatch, {
            [styles.typeaheadMatchActive]: isSelected
        });
        return (
            <li
                key={index}
                id={item.code}
                role="option"
                aria-selected={isSelected}
                onClick={() => onItemSelected(item)}
                onMouseEnter={() => selectItemOnHover(index)}
                className={entryClassName}
                onKeyDown={handleKeyDown}
            >
                {renderableItem}
            </li>
        );
    };

    const listClassName = classNames('gw-dropdown-menu', styles.typeaheadResults);
    const itemsToRender = _(items)
        .take(maxResults || Infinity)
        .value()
        .map(renderDropdownElement);

    return !_.isEmpty(itemsToRender) && (
        <div className="gw-open sgTypeAheadResultsContainer">
            {topDropdownText && <p id="topDropdownText">{topDropdownText}</p>}
            <ul
                id={`${id}_typeaheadDropdownList`}
                role="listbox"
                ref={setupDropdownRef}
                onFocus={handleOnFocus}
                tabIndex={0}
                className={listClassName}
                onKeyDown={handleKeyDown}
            >
                {itemsToRender}
            </ul>
            {bottomDropdownText && <p id="bottomDropdownText">{bottomDropdownText}</p>}
        </div>
    );
}

export default withTracking(SgTypeAheadDropdownComponent);
